// De bronvermelding van de chat, gedeeld met het Plan (T1, owner 04-11: "gebruik de
// bronvermelding van hoe we het in de chat ook doen. Dit moet consistent zijn").
//
// Hier staan de inline uitgeverschip (UitgeverPill), de parser die [[bron:id|titel]]
// in een tekst omzet (tekstMetVerwijzingenEnGetallen), de bronpil (BronPill) en de
// bronnenrij onder een antwoord (Bronnen). Ze stonden in ChatPopup.tsx en zijn
// letterlijk verhuisd: de chat en de plantekst gebruiken nu hetzelfde stuk code,
// dus een chip in het plan ziet er precies zo uit als een chip in de chat.

import { type ReactNode, useLayoutEffect, useRef, useState } from "react";

import type { ChatResponse, KaartLanding } from "../contracts";
import { jaarSoortTekst } from "../lib/schermen";
import { metNummerChips } from "../nieuw/trendlaag/Rust";
import { BetaalmuurPil, heeftBewijsvelden, WaaropPil, WIE_KORT, WIE_WOORD, WiePil, wieTelling } from "./Bewijs";
import type { PaneelDoel } from "./ChatBlokken";
import { GeteldBadge, LaagChip, alsGetal, type Telling } from "./ChatBonnetje";
import { color, radius, size, weight } from "./chatTokens";
import { MARKER_BRON } from "./Verwijzing";

export type Citation = ChatResponse["citations"][number];
export type FundingBron = NonNullable<ChatResponse["fundingBronnen"]>[number];


/**
 * De inline bronpill mét layover (Chat 2.0, besluiten B4 + P6; owner 26-08:
 * "houdt dit consistent" met de feed-redesign). Zelfde idioom als Bronpil.tsx
 * van de maandredactie: een chip met de uitgeversnamen (poetsronde 28-08:
 * tot drie náást elkaar in plaats van één naam met +N — de compressie
 * verstopte gemeten negen van de elf bronnen), en bij de klik een zwevend
 * paneel — "N bronnen onder deze alinea" — met per bron het gezagscijfer en
 * bovenaan het verbatim citaat. Wat de chat eraan toevoegt: een rij
 * aanklikken opent het brondetail ín de chat (B5), en de voet wijst naar de
 * bronnenrij onder het antwoord.
 *
 * BEWUST GESPIEGELD, NIET GEÏMPORTEERD: Bronpil.tsx is van de redactiesessie
 * (die er op dit moment in schrijft) en kent geen klik-callbacks; twee
 * schrijvers in één component is de W15-fout. Zelfde maten en tokens, eigen
 * gedrag.
 */
export function UitgeverPill({
  citaties,
  onPaneel,
  onBron,
}: {
  /** De gebundelde bronnen achter deze zin, in leesvolgorde. */
  citaties: Citation[];
  onPaneel?: (doel: PaneelDoel) => void;
  onBron?: (documentId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  // Het zwevende paneel hangt links onder de chip. Staat de chip ver naar
  // rechts, dan stak het paneel buiten het berichtenvak en ging dat vak
  // zijwaarts schuiven (gemeten 01-10 op 390 px: 14 px). Het paneel schuift nu
  // zoveel naar links als nodig is om binnen het vak te blijven.
  const paneelRef = useRef<HTMLSpanElement>(null);
  const [naarLinks, setNaarLinks] = useState(0);
  useLayoutEffect(() => {
    if (!open) {
      setNaarLinks(0);
      return;
    }
    const el = paneelRef.current;
    if (!el || typeof el.getBoundingClientRect !== "function") return;
    const vak = el.closest<HTMLElement>("[data-chat-berichten]");
    const grens = vak ? vak.getBoundingClientRect().left + vak.clientWidth : window.innerWidth;
    const over = el.getBoundingClientRect().right - (grens - 8);
    if (over > 0) setNaarLinks(Math.ceil(over));
  }, [open]);
  const gezagen = citaties
    .map((c) => c.authority)
    .filter((x): x is number => typeof x === "number");
  const gemiddeld = gezagen.length
    ? (gezagen.reduce((a, b) => a + b, 0) / gezagen.length).toFixed(1).replace(".", ",")
    : null;
  // Methode v3: dragen de citaties het wie-veld, dan telt de kop per wie en
  // toont het vakje INST/VAK/BEDR in plaats van het gezagscijfer.
  const v3 = citaties.some((c) => heeftBewijsvelden(c));
  const telling = v3 ? wieTelling(citaties) : null;
  const label = citaties.map((c) => c.title || c.publisher).filter(Boolean).join(" · ");
  // Poetsronde 28-08: de chip toont de UITGEVERS naast elkaar in plaats van
  // één naam met "+4" — gemeten haalde een antwoord met elf bronnen er maar
  // twee zichtbaar in de tekst, en dat was precies de klacht "ik zie weinig
  // bronnen". Uniek per uitgever, hoogstens drie namen, de rest als +N.
  const uitgevers = [...new Set(citaties.map((c) => c.publisher).filter(Boolean))];
  const getoond = uitgevers.slice(0, 3);
  const restUitgevers = uitgevers.length - getoond.length;

  return (
    <span style={{ position: "relative", display: "inline-block" }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-label={`Bron: ${label}`}
        title={label}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          fontSize: 10.5,
          fontWeight: weight.medium,
          fontFamily: "inherit",
          color: open ? color.surface : color.soft,
          background: open ? color.ink : color.surfaceQuote,
          border: `1px solid ${open ? color.ink : color.border}`,
          borderRadius: 5,
          padding: "1.5px 7px",
          marginLeft: 4,
          lineHeight: 1.55,
          cursor: "pointer",
          verticalAlign: "baseline",
        }}
      >
        {getoond.length ? getoond.join(" · ") : "bron"}
        {restUitgevers > 0 && (
          <span style={{ color: open ? "#cbd5e1" : color.faint, fontWeight: weight.regular }}>
            +{restUitgevers}
          </span>
        )}
      </button>

      {open && (
        <>
          <span
            onClick={() => setOpen(false)}
            style={{ position: "fixed", inset: 0, zIndex: 40 }}
          />
          <span
            ref={paneelRef}
            style={{
              position: "absolute",
              top: "calc(100% + 6px)",
              left: -naarLinks,
              width: 340,
              maxWidth: "min(340px, calc(100vw - 40px))",
              background: color.surface,
              border: `1px solid ${color.border}`,
              borderRadius: radius.block,
              boxShadow: "0 10px 30px rgba(17,24,39,.13)",
              padding: "11px 13px",
              zIndex: 50,
              display: "block",
              textAlign: "left",
              whiteSpace: "normal",
            }}
          >
            <span
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 10,
                marginBottom: 6,
              }}
            >
              <span
                style={{
                  fontSize: size.columnLabel,
                  fontWeight: weight.medium,
                  letterSpacing: "0.07em",
                  textTransform: "uppercase",
                  color: color.faint,
                }}
              >
                {/* "alinea", niet "zin": de promptregel markeert per alinea
                    (chat.py) — de kop zei "zin" en dat was gejokt. */}
                {citaties.length} {citaties.length === 1 ? "bron" : "bronnen"} onder deze alinea
              </span>
              {v3
                ? telling && (
                    <span style={{ fontSize: size.label, color: color.faint }}>{telling}</span>
                  )
                : gemiddeld && (
                    <span style={{ fontSize: size.label, color: color.faint }}>
                      gezag {gemiddeld}
                    </span>
                  )}
            </span>

            {/* Álle bronnen van de groep, scrollend — de afkap op drie
                verstopte bewijs dat al gevonden was (poetsronde 28-08). */}
            <span style={{ display: "block", maxHeight: 300, overflowY: "auto" }}>
            {citaties.map((c, i) => (
              <button
                key={c.documentId}
                onClick={() => {
                  setOpen(false);
                  if (onPaneel) onPaneel({ soort: "bron", id: c.documentId });
                  else onBron?.(c.documentId);
                }}
                style={{
                  display: "grid",
                  gridTemplateColumns: "26px 1fr",
                  gap: 9,
                  width: "100%",
                  padding: "7px 0",
                  background: "none",
                  border: "none",
                  borderTop: i === 0 ? "none" : `1px solid ${color.lineFaint}`,
                  font: "inherit",
                  textAlign: "left",
                  cursor: "pointer",
                }}
              >
                <span
                  style={{
                    width: 26,
                    height: 20,
                    borderRadius: 5,
                    background: color.surfaceQuote,
                    border: `1px solid ${color.border}`,
                    fontSize: 9.5,
                    fontWeight: weight.bold,
                    color: color.soft,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {v3
                    ? (c.wie ? WIE_KORT[c.wie] : "—")
                    : typeof c.authority === "number"
                      ? String(c.authority).replace(".0", "")
                      : "—"}
                </span>
                <span style={{ display: "block", minWidth: 0 }}>
                  <span
                    style={{
                      display: "block",
                      fontSize: 12,
                      fontWeight: weight.medium,
                      color: color.ink,
                    }}
                  >
                    {c.publisher}
                  </span>
                  {c.title && (
                    <span
                      style={{ display: "block", fontSize: size.label, color: color.faint }}
                    >
                      {c.title.slice(0, 52)}
                    </span>
                  )}
                  {c.achterBetaalmuur && (
                    <span style={{ display: "block", marginTop: 3 }}>
                      <BetaalmuurPil bron={c} />
                    </span>
                  )}
                  {i === 0 && c.quote && (
                    <span
                      style={{
                        display: "block",
                        marginTop: 5,
                        borderLeft: `2px solid ${color.border}`,
                        paddingLeft: 9,
                        fontSize: 11.5,
                        color: color.muted,
                        fontStyle: "italic",
                        lineHeight: 1.55,
                      }}
                    >
                      “{c.quote.replace(/\s+/g, " ").slice(0, 180)}
                      {c.quote.length > 180 ? "…" : ""}”
                    </span>
                  )}
                </span>
              </button>
            ))}
            </span>

            <span
              style={{
                display: "block",
                paddingTop: 8,
                marginTop: 4,
                borderTop: `1px solid ${color.lineFaint}`,
                fontSize: size.label,
                color: color.faint,
              }}
            >
              klik een bron voor het detail · de hele lijst staat onder het antwoord
            </span>
          </span>
        </>
      )}
    </span>
  );
}

/** Wat de renderer nodig heeft naast de tekst zelf. */
export type Leescontext = {
  /** documentId -> citatie, voor de inline bronpills mét layover (B4). */
  bronnen?: Record<string, Citation>;
  /** Opent de popover in de bronnenrij onder het antwoord. */
  onBron?: (documentId: string) => void;
  /** Opent het brondetail ín de chat (B5). */
  onPaneel?: (doel: PaneelDoel) => void;
  tellingen: Telling[];
  /** Elke telling krijgt hoogstens één badge: de eerste keer dat zijn getal
   *  letterlijk in het antwoord staat. Anders wordt elke herhaling onderstreept
   *  en leest het antwoord als een formulier. */
  gebruikt: Set<Telling>;
  /** Trendlaag (schakelaar aan, editie 15 / CH-A): de nummers [n] die in het
   *  kader van dit antwoord bestaan worden klikbare chipjes. Afwezig = [n]
   *  blijft tekst, zoals in de uit-stand. */
  nummers?: { bekend: ReadonlySet<number>; onNummer?: (nr: number) => void };
  /** Het antwoord stroomt nog binnen: een bronverwijzing krijgt al de vorm van
   *  de pil (zonder uitgever, die komt met het slotantwoord), zodat de opmaak
   *  niet pas aan het eind verschijnt (testfeedback 03-11, rij 5). */
  streaming?: boolean;
};

/**
 * Eén tekststuk zonder markdown: eerst de verwijzingen eruit, dán de getallen.
 *
 * DIE VOLGORDE IS NIET VRIJBLIJVEND. Een marker draagt een uuid en een uuid
 * draagt cijfers; wie eerst op getallen splitst knipt de marker doormidden en
 * zet er rauwe machinetaal voor terug — precies de fout die dit project al
 * twee keer maakte.
 */
/** Een marker zonder |titel matcht MARKER_BRON niet en zou als rauwe uuid in
 *  beeld staan (gemeten 27-08, opname 98piuelaxjx3). De backend repareert dit
 *  al bij de bron; dit net vangt wat er nog doorheen glipt — oudere bewaarde
 *  gesprekken bijvoorbeeld. De kale vorm draagt geen leesbare informatie,
 *  dus weghalen verliest niets. */
const KALE_MARKER = /\[\[(?:trend|skill|bron):[0-9a-fA-F-]{8,}?\]\]/g;

export function tekstMetVerwijzingenEnGetallen(
  ruweTekst: string,
  sleutel: string,
  ctx: Leescontext,
): ReactNode[] {
  // Een onafgemaakte marker aan het eind ("[[" zonder sluiting, gezien in de
  // kijkpaarproef 01-10) is machinetaal zonder inhoud: weg ermee.
  const tekst = ruweTekst.replace(KALE_MARKER, "").replace(/\[\[[^\]\n]*$/, "");
  const uit: ReactNode[] = [];
  const marker = new RegExp(MARKER_BRON, "g");
  let laatste = 0;
  let m: RegExpExecArray | null;

  const gewoneTekst = (stuk: string, k: string) => {
    if (!stuk) return;
    // Trendlaag: eerst de nummers [n] eruit (ook die dragen cijfers), dan de getallen.
    if (ctx.nummers) {
      metNummerChips(stuk, ctx.nummers.bekend, ctx.nummers.onNummer, getallen, (chip) => uit.push(chip), k);
      return;
    }
    getallen(stuk, k);
  };
  const getallen = (stuk: string, k: string) => {
    if (!stuk) return;
    // Getallen die we écht geteld hebben krijgen hun badge; de rest blijft tekst.
    const delen = stuk.split(/(\d[\d.]*)/g);
    for (const [i, deel] of delen.entries()) {
      const waarde = alsGetal(deel);
      const telling =
        waarde === null
          ? undefined
          : ctx.tellingen.find((t) => t.aantal === waarde && !ctx.gebruikt.has(t));
      if (telling) {
        ctx.gebruikt.add(telling);
        uit.push(
          <span key={`${k}-g${i}`} style={{ whiteSpace: "nowrap" }}>
            {deel}
            <GeteldBadge telling={telling} />
          </span>,
        );
      } else if (deel) {
        // Kale tekst blijft kale tekst: een extra <span> eromheen zou de
        // opmaak eromheen (vet, kop) een niveau dieper duwen en is nergens
        // voor nodig.
        uit.push(deel);
      }
    }
  };

  // Eerst alle markers verzamelen: opeenvolgende BRON-markers (alleen
  // witruimte ertussen) bundelen tot één pill met +N — het Perplexity-idioom,
  // en het antwoord op "waar horen die vijf bronnen bij" uit de opname.
  type Marker = { index: number; lengte: number; soort: string; id: string; titel: string };
  const markers: Marker[] = [];
  while ((m = marker.exec(tekst)) !== null) {
    markers.push({
      index: m.index,
      lengte: m[0].length,
      soort: m[1] ?? "",
      id: m[2] ?? "",
      titel: m[3] ?? "",
    });
  }
  let i = 0;
  while (i < markers.length) {
    const mk = markers[i]!;
    gewoneTekst(tekst.slice(laatste, mk.index), `${sleutel}-v${mk.index}`);
    if (mk.soort === "bron") {
      const groep = [mk];
      let eind = mk.index + mk.lengte;
      let j = i + 1;
      while (
        j < markers.length &&
        markers[j]!.soort === "bron" &&
        tekst.slice(eind, markers[j]!.index).trim() === ""
      ) {
        groep.push(markers[j]!);
        eind = markers[j]!.index + markers[j]!.lengte;
        j += 1;
      }
      // Eerst op id; lukt dat niet, dan op TITEL. Het model citeert soms een
      // bron die het via een trenddetail las (titel bekend, id verhaspeld) —
      // zonder deze terugval bleef die titel als los zinsdeel achter de zin
      // hangen ("Thoughtworks Technology Radar", opname 27-08 frame 1:48).
      const alleBronnen = Object.values(ctx.bronnen ?? {});
      const bekend = groep
        .map(
          (g) =>
            ctx.bronnen?.[g.id] ??
            alleBronnen.find(
              (c) => c.title && g.titel && c.title.toLowerCase() === g.titel.trim().toLowerCase(),
            ),
        )
        .filter((c): c is NonNullable<typeof c> => Boolean(c));
      if (bekend.length > 0) {
        uit.push(
          <UitgeverPill
            key={`${sleutel}-b${mk.index}`}
            citaties={bekend}
            onPaneel={ctx.onPaneel}
            onBron={ctx.onBron}
          />,
        );
      } else if (ctx.streaming) {
        uit.push(
          <span key={`${sleutel}-b${mk.index}`} data-chat="bron-wacht" aria-hidden style={bronWachtStijl}>
            bron{groep.length > 1 ? ` +${groep.length - 1}` : ""}
          </span>,
        );
      } else {
        // Onbekende bron(nen): de marker verdwijnt en de titel blijft
        // leesbaar. Liever niets dan een verwijzing die nergens heen gaat.
        uit.push(
          <span key={`${sleutel}-b${mk.index}`}>
            {groep.map((g) => g.titel).filter(Boolean).join(" ")}
          </span>,
        );
      }
      laatste = eind;
      i = j;
      continue;
    }
    // Trend- en skillmarkers renderen als KALE TITEL in de zin (owner 26-08:
    // inline onderstrepingen maken het rommelig). De klikbare weg loopt via
    // het kaartenblok onder de tekst; de marker zelf blijft in de data
    // bestaan, want hij voedt dat blok.
    uit.push(<span key={`${sleutel}-m${mk.index}`}>{mk.titel}</span>);
    laatste = mk.index + mk.lengte;
    i += 1;
  }
  gewoneTekst(tekst.slice(laatste), `${sleutel}-e`);
  return uit;
}

function Gezag({ waarde }: { waarde: number }) {
  return (
    <span
      title="Gezagscijfer van de uitgever, uit de rubriek (dimensie source_credibility)."
      style={{
        fontSize: 10.5,
        fontWeight: weight.medium,
        color: color.body,
        background: color.surface,
        border: `1px solid ${color.border}`,
        borderRadius: radius.pill,
        padding: "2px 8px",
        whiteSpace: "nowrap",
      }}
    >
      gezag {waarde.toFixed(1).replace(".", ",")}
    </span>
  );
}

/**
 * Eén bron als pill. De uitgever en zijn gezagscijfer staan in de rij; alles
 * wat erachter zit (titel, fragment, laag, sectie, de twee doorkliks) komt bij
 * de klik.
 */
export function BronPill({
  citaat,
  open,
  onKlik,
}: {
  citaat: Citation;
  open: boolean;
  onKlik: () => void;
}) {
  return (
    <button
      onClick={onKlik}
      title={citaat.title || citaat.url || "bron zonder titel"}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        maxWidth: 260,
        background: open ? color.ink : color.surface,
        border: `1px solid ${open ? color.ink : color.border}`,
        borderRadius: radius.pill,
        padding: "4px 10px",
        fontSize: 11.5,
        fontFamily: "inherit",
        color: open ? color.surface : color.body,
        cursor: "pointer",
        transition: "all 0.12s",
      }}
      onMouseEnter={(e) => {
        if (!open) e.currentTarget.style.borderColor = color.soft;
      }}
      onMouseLeave={(e) => {
        if (!open) e.currentTarget.style.borderColor = color.border;
      }}
    >
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {citaat.publisher}
      </span>
      {/* H2 (bouwrun R1): het jaar staat op de pil zelf; de soort in de
          geopende bron. Zonder veld: niets (het oude scherm). */}
      {citaat.jaar && (
        <span
          data-testid="bron-jaar"
          title={jaarSoortTekst(citaat) ?? undefined}
          style={{ fontSize: 10, color: open ? color.surface : color.faint, flexShrink: 0 }}
        >
          {citaat.jaar === "onbekend" ? "jaar ?" : citaat.jaar}
        </span>
      )}
      {/* Methode v3: het wie-woord in de labelkleur (instituut blauw, vakblad
          amber, bedrijf neutraal) in plaats van het gezagscijfer. */}
      {citaat.wie ? (
        <span
          style={{
            fontSize: 10,
            fontWeight: weight.medium,
            color: open
              ? color.surface
              : citaat.wie === "instituut"
                ? color.accentBlue
                : citaat.wie === "vakmedia"
                  ? color.accentAmber
                  : color.soft,
            flexShrink: 0,
          }}
        >
          {citaat.wie === "instituut" ? "✓ " : ""}
          {WIE_WOORD[citaat.wie] ?? citaat.wie}
        </span>
      ) : typeof citaat.authority === "number" && !heeftBewijsvelden(citaat) && (
        <span
          style={{
            fontSize: 10,
            fontWeight: weight.medium,
            color: open ? color.surface : color.faint,
            flexShrink: 0,
          }}
        >
          {citaat.authority.toFixed(1).replace(".", ",")}
        </span>
      )}
      {/* Besluit K2 (reparatie 06-10): ook op de kleine pil zichtbaar. */}
      {citaat.achterBetaalmuur && (
        <span
          data-testid="betaalmuur-kort"
          title={`${citaat.betaalmuurLabel || "achter een betaalmuur"}: telt niet mee in de weging`}
          style={{
            fontSize: 10,
            fontWeight: weight.medium,
            color: open ? color.surface : color.accentAmber,
            flexShrink: 0,
          }}
        >
          betaalmuur
        </span>
      )}
    </button>
  );
}

/**
 * De bronnen onder een antwoord.
 *
 * HERONTWERP 2026-08-25 (owner-opname 0:52 "the way it gives you sources,
 * that's how we need to do this as well... sources like this with these
 * pills"). Was: een verticale stapel kaarten met titel, uitgever, laag,
 * gezag en fragment - informatierijk, maar het duwde het antwoord van het
 * scherm. Nu: één compacte rij pills met de uitgever en zijn gezagscijfer,
 * plus een teller. ALLE INFORMATIE BLIJFT, hij verhuist naar de tweede klik.
 *
 * Bron-per-claim (een pill achter de zin die hij draagt, zoals Perplexity) is
 * bewust een aparte vervolgstap: dat raakt de prompt, de parser en de meetlat.
 */
/**
 * De subsidieregelingen onder een antwoord, als pills naast de bron-chips.
 *
 * Owner-opname rcgukmx6hc8l 3:40: een funding-antwoord zonder bronnen "seems a
 * bit weird". Ze staan bewust in een EIGEN rij en niet tussen de documentbronnen:
 * een regeling is geen document met een citaat, en ze door elkaar zetten zou
 * suggereren dat er een fragment achter zit dat er niet is. De doelgroep-eis
 * staat op de pill, want dat is de vraag die beslist of de regeling je aangaat.
 */
function FundingBronnen({
  bronnen,
  onOpen,
  onPaneel,
}: {
  bronnen: FundingBron[];
  onOpen?: (landing: KaartLanding) => void;
  /** Opent de regeling ín de chat (B5); zonder deze prop naar de Funding-tab. */
  onPaneel?: (doel: PaneelDoel) => void;
}) {
  if (bronnen.length === 0) return null;
  return (
    <div style={{ marginTop: 10 }}>
      <div
        style={{
          fontSize: 10.5,
          fontWeight: weight.medium,
          color: color.faint,
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          marginBottom: 6,
        }}
      >
        Regelingen {bronnen.length}
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {bronnen.map((b) => (
          <button
            key={b.id}
            onClick={() =>
              onPaneel
                ? onPaneel({ soort: "funding", id: b.id })
                : onOpen?.({ soort: "tab", tab: "funding" })
            }
            title={
              [
                b.verstrekker,
                b.aanvraagperiode,
                b.doelgroepEis,
                b.nagekeken === false ? "nieuw gevonden, nog niet nagekeken" : null,
              ]
                .filter(Boolean)
                .join(" · ") || undefined
            }
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              maxWidth: 320,
              background: color.surface,
              border: `1px solid ${color.border}`,
              borderRadius: radius.pill,
              padding: "5px 12px",
              fontSize: 12,
              fontFamily: "inherit",
              color: color.body,
              cursor: "pointer",
            }}
          >
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {b.naam}
            </span>
            {b.status && (
              <span style={{ color: color.faint, flexShrink: 0 }}>· {b.status}</span>
            )}
            {/* Review fase 6: een vondst draagt haar voorbehoud op de chip. */}
            {b.nagekeken === false && (
              <span style={{ color: color.accentAmber, flexShrink: 0 }}>
                · nog niet nagekeken
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

// Geëxporteerd voor de betaalmuurwachter (Betaalmuur.test.tsx).
export function Bronnen({
  citaten,
  tools,
  fundingBronnen = [],
  geciteerdIds = [],
  onOpen,
  onPaneel,
  openId: openVanBuiten,
  onOpenId,
}: {
  citaten: Citation[];
  tools: string[];
  fundingBronnen?: FundingBron[];
  /** De documentIds die als inline pill in de tekst staan, in leesvolgorde.
   *  Splitst de rij in "geciteerd in de tekst" en "ook geraadpleegd" (P7):
   *  zo klopt elk getal met wat je in het antwoord ziet — het oude "Bronnen
   *  24" boven acht inline pills was precies de onnavolgbaarheid uit de
   *  opname. */
  geciteerdIds?: string[];
  onOpen?: (landing: KaartLanding) => void;
  /** Opent het brondetail ín de chat (B5); zonder deze prop valt de knop
   *  terug op de Bronnen-tab. */
  onPaneel?: (doel: PaneelDoel) => void;
  /** Gedeeld met de bron-pills in de tekst: één popover, één klikdoel. */
  openId?: string | null;
  onOpenId?: (id: string | null) => void;
}) {
  const [eigenOpen, setEigenOpen] = useState<string | null>(null);
  const openId = openVanBuiten !== undefined ? openVanBuiten : eigenOpen;
  const setOpenId = onOpenId ?? setEigenOpen;
  const [alle, setAlle] = useState(false);

  if (citaten.length === 0) {
    // Wél regelingen, geen documentcitaat: dan is dit antwoord niet bronloos.
    if (fundingBronnen.length > 0) {
      return (
        <FundingBronnen bronnen={fundingBronnen} onOpen={onOpen} onPaneel={onPaneel} />
      );
    }
    return (
      <p
        style={{
          fontSize: 12,
          color: color.faint,
          fontStyle: "italic",
          marginTop: 8,
          lineHeight: 1.5,
        }}
      >
        Dit antwoord steunt op de conclusies in het dashboard, niet op een
        letterlijk citaat uit een document
        {tools.length > 0 ? ` (gebruikt: ${tools.join(", ")})` : ""}. Vraag om de
        bron als je het citaat wilt zien.
      </p>
    );
  }

  const uit = citaten.find((c) => c.documentId === openId) ?? null;
  // LIVE GEMETEN 2026-08-25: een antwoord haalde 30 bronnen op. Dertig pills
  // vullen zeven regels en dan is de rij weer een blok - precies wat deze
  // ingreep moest oplossen. Een greep plus een teller; de rest is één klik
  // weg en er verdwijnt niets stilzwijgend.
  //
  // CHAT 2.0: de rij splitst in "geciteerd in de tekst" (elke pill staat óók
  // inline, in leesvolgorde) en "+N ook geraadpleegd". Zonder die splitsing
  // klopte de kop niet met de tekst — Perplexity heeft hetzelfde gat en
  // maskeert het; wij zeggen het.
  const TOON = 6;
  const gesplitst = geciteerdIds.length > 0;
  const geciteerd = gesplitst
    ? geciteerdIds
        .map((id) => citaten.find((c) => c.documentId === id))
        .filter((c): c is Citation => Boolean(c))
    : citaten;
  const geraadpleegd = gesplitst
    ? citaten.filter((c) => !geciteerdIds.includes(c.documentId))
    : [];
  const zichtbaar = alle ? geciteerd : geciteerd.slice(0, TOON);
  const rest = geciteerd.length - zichtbaar.length;
  const extra = alle ? geraadpleegd : [];

  return (
    <div style={{ marginTop: 12 }}>
      <div
        style={{
          display: "flex",
          gap: 6,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <span
          style={{
            fontSize: size.label,
            fontWeight: weight.medium,
            color: color.faint,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            marginRight: 2,
          }}
        >
          {gesplitst ? `Geciteerd in de tekst · ${geciteerd.length}` : `Bronnen ${citaten.length}`}
        </span>
        {zichtbaar.map((c) => (
          <BronPill
            key={c.documentId}
            citaat={c}
            open={c.documentId === openId}
            onKlik={() => setOpenId(c.documentId === openId ? null : c.documentId)}
          />
        ))}
        {rest > 0 && (
          <button
            onClick={() => setAlle(true)}
            style={{
              background: color.lineFaint,
              border: `1px solid ${color.border}`,
              borderRadius: radius.pill,
              padding: "4px 10px",
              fontSize: 11.5,
              fontFamily: "inherit",
              color: color.soft,
              cursor: "pointer",
            }}
          >
            +{rest}
          </button>
        )}
        {gesplitst && geraadpleegd.length > 0 && !alle && (
          <button
            onClick={() => setAlle(true)}
            title="Deze bronnen zijn wél opgehaald en gelezen, maar het antwoord citeert ze niet bij een claim."
            style={{
              background: color.surface,
              border: `1px dashed ${color.disabled}`,
              borderRadius: radius.pill,
              padding: "4px 11px",
              fontSize: 11.5,
              fontFamily: "inherit",
              color: color.faint,
              cursor: "pointer",
            }}
          >
            +{geraadpleegd.length} ook geraadpleegd
          </button>
        )}
        {extra.map((c) => (
          <BronPill
            key={c.documentId}
            citaat={c}
            open={c.documentId === openId}
            onKlik={() => setOpenId(c.documentId === openId ? null : c.documentId)}
          />
        ))}
        {alle && (geciteerd.length > TOON || geraadpleegd.length > 0) && (
          <button
            onClick={() => setAlle(false)}
            style={{
              background: "none",
              border: "none",
              padding: "4px 6px",
              fontSize: 11.5,
              fontFamily: "inherit",
              color: color.faint,
              cursor: "pointer",
            }}
          >
            minder
          </button>
        )}
      </div>

      {uit && (
        <div
          style={{
            marginTop: 8,
            background: color.surfaceAlt,
            borderRadius: radius.input,
            padding: "10px 12px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 8,
              justifyContent: "space-between",
            }}
          >
            <div style={{ minWidth: 0 }}>
              {/* 196 van de 565 bronnen dragen geen titel; die tonen hun URL.
                  Net als op de bronnenlijst staat de reden erbij, zodat een
                  URL niet stilzwijgend voor een titel doorgaat. */}
              <div
                title={
                  uit.title
                    ? undefined
                    : "Dit document droeg geen titel; daarom staat hier de URL."
                }
                style={{
                  fontSize: size.meta,
                  fontWeight: weight.medium,
                  color: uit.title ? color.ink : color.soft,
                  lineHeight: 1.4,
                }}
              >
                {uit.title || uit.url || "bron zonder titel"}
              </div>
              <div
                style={{
                  fontSize: 11.5,
                  color: color.faint,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  flexWrap: "wrap",
                  marginTop: 2,
                }}
              >
                <span>{uit.publisher}</span>
                {jaarSoortTekst(uit) && (
                  <span data-testid="bron-jaar-soort">{jaarSoortTekst(uit)}</span>
                )}
                {/* Het laag-label beantwoordt de vraag waar faalgeval
                    T1-DIR-3 op stukliep: van wie komt dit, en hoe zwaar
                    weegt het? Die as zat al in de data en stond niet op
                    het scherm. */}
                <LaagChip laag={uit.laag} sectie={uit.sectie} />
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
              {/* Methode v3: wie + waarop in plaats van de gezag-pil. */}
              {heeftBewijsvelden(uit) ? (
                <>
                  <WiePil bron={uit} />
                  <WaaropPil bron={uit} />
                </>
              ) : (
                typeof uit.authority === "number" && <Gezag waarde={uit.authority} />
              )}
              {/* Besluit K2 (reparatie 06-10): afgeschermd document. */}
              <BetaalmuurPil bron={uit} />
              <button
                onClick={() => setOpenId(null)}
                aria-label="Bron sluiten"
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  fontSize: 12,
                  color: color.disabled,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                ✕
              </button>
            </div>
          </div>

          {uit.quote ? (
            <div style={{ marginTop: 8 }}>
              {/* "Fragment", niet "citaat": dit is het stuk document dat de
                  zoekvraag ophaalde, letterlijk zoals het is opgeslagen. Bij
                  webpagina's zit daar soms menutekst in - die halen we er
                  niet uit, want dan is het niet meer verbatim en kun je het
                  niet meer terugvinden in het origineel. */}
              <div
                style={{
                  fontSize: 10.5,
                  fontWeight: weight.medium,
                  color: color.faint,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  marginBottom: 4,
                }}
              >
                Fragment uit het document
                {/* Waar in het document, uit de structuurkaart. Een citaat
                    uit "Conclusies" weegt anders dan een uit een bijlage of
                    een cookiemelding. Ontbreekt het label, dan staat er
                    niets - geen verzonnen plaats. */}
                {uit.sectie ? (
                  <span style={{ textTransform: "none", letterSpacing: 0 }}>
                    {" · "}
                    {uit.sectie}
                  </span>
                ) : null}
              </div>
              <p
                style={{
                  fontSize: size.meta,
                  color: color.muted,
                  lineHeight: 1.6,
                  borderLeft: `2px solid ${color.border}`,
                  paddingLeft: 10,
                  whiteSpace: "pre-wrap",
                }}
              >
                {uit.quote}
              </p>
            </div>
          ) : (
            <p style={{ fontSize: 11.5, color: color.faint, fontStyle: "italic", marginTop: 6 }}>
              Van deze bron is geen letterlijk fragment opgehaald; hij komt uit
              de conclusies van het dashboard.
            </p>
          )}

          <div style={{ display: "flex", gap: 14, marginTop: 8 }}>
            {(onPaneel || onOpen) && (
              // Het brondetail opent ÍN de chat (B5): het gesprek blijft
              // staan. Zonder in-chat paneel valt de knop terug op de
              // Bronnen-tab; het externe origineel blijft ernaast staan.
              <button
                onClick={() =>
                  onPaneel
                    ? onPaneel({ soort: "bron", id: uit.documentId })
                    : onOpen!({ soort: "paneel", tab: "sources", id: uit.documentId })
                }
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  font: "inherit",
                  fontSize: 12,
                  color: color.accentBlue,
                  cursor: "pointer",
                }}
              >
                Bekijk deze bron →
              </button>
            )}
            {uit.url && (
              <a
                href={uit.url}
                target="_blank"
                rel="noreferrer"
                style={{
                  fontSize: 12,
                  color: color.accentBlue,
                  textDecoration: "none",
                }}
              >
                Origineel openen →
              </a>
            )}
          </div>
        </div>
      )}

      {/* Regelingen staan in hun eigen rij ONDER de documentbronnen: ze dragen
          geen citaat, dus ertussen zetten zou een fragment suggereren dat er
          niet is. */}
      <FundingBronnen bronnen={fundingBronnen} onOpen={onOpen} onPaneel={onPaneel} />
    </div>
  );
}

const bronWachtStijl = {
  display: "inline-flex",
  alignItems: "center",
  fontSize: 10.5,
  fontWeight: weight.medium,
  color: color.faint,
  background: color.surfaceQuote,
  border: `1px solid ${color.border}`,
  borderRadius: 5,
  padding: "1.5px 7px",
  marginLeft: 4,
  lineHeight: 1.55,
  verticalAlign: "baseline",
} as const;
